import React, { useEffect, useState } from 'react';
import { useUser } from './UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from './ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FUNCTION_NAMES } from '../constants';
import { API_BASE_URL } from '../config';
import { fetchWithRetry } from '../utils/tourUtils';
import { PlusCircle, X, Users, Building2, FolderOpen, RefreshCw, Shield, UserCog, KeyRound, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const UserClientsManager = () => {
  const { selectedUserPermissions, setSelectedUser, setSelectedUserPermissions, hasPermission } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [userClients, setUserClients] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  
  // User-Client state
  const [selectedUserUserClient, setSelectedUserUserClient] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [existingUserClientIds, setExistingUserClientIds] = useState([]);
  
  // User-Project state
  const [selectedUserUserProject, setSelectedUserUserProject] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [existingUserProjectIds, setExistingUserProjectIds] = useState([]);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    userId: null,
    clientId: null,
    projectId: null,
    id: null,
    type: null, // 'client' or 'project'
  });
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [permissionDialog, setPermissionDialog] = useState({
    open: false,
    userId: null,
    username: '',
    currentPermissions: [],
    newPermissions: [],
  });
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    userId: null,
    username: '',
    newPassword: '',
    confirmPassword: '',
    showPassword: false,
  });

  // Permission options
  const permissions = [
    { value: 'ADMIN_USER', label: 'Admin' },
    { value: 'UPLOADER_IMAGE', label: 'Uploader' },
    { value: 'FULL_EDITOR', label: 'Full Editor' },
    { value: 'FULL_VIEWER', label: 'Full Viewer' },
    { value: 'CLIENT_VIEWER', label: 'Client Viewer' },
  ];
  // Redirect non-admin users
  useEffect(() => {
    if (!hasPermission('ADMIN_USER')) {
      toast({
        title: 'Access Denied',
        description: 'Only administrators can access this page.',
        variant: 'destructive',
      });
      navigate('/');
    }
  }, [selectedUserPermissions, navigate, hasPermission]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'user.findMany',
          payload: {},
        },
        label: 'fetch_users',
      });
      setUsers(result);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Error',
        description: 'Failed to load users',
        variant: 'destructive',
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'client.findMany',
          payload: {},
        },
        label: 'fetch_clients',
      });
      setClients(result);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        title: 'Error',
        description: 'Failed to load clients',
        variant: 'destructive',
      });
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchProjects = async () => {
    setLoadingProjects(true);
    try {
      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'project.findMany',
          payload: {},
        },
        label: 'fetch_projects',
      });
      setProjects(result);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load projects',
        variant: 'destructive',
      });
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchUserClients = async () => {
    try {
      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'userClient.findMany',
          payload: {
            include: {
              user: true,
              client: true,
            },
          },
        },
        label: 'fetch_user_clients',
      });
      setUserClients(result);
      console.log('User clients:', result); // Log to see the actual structure
    } catch (error) {
      console.error('Error fetching user-client associations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user-client associations',
        variant: 'destructive',
      });
    }
  };

  const fetchUserProjects = async () => {
    try {
      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'userProject.findMany',
          payload: {
            include: {
              user: true,
              project: true,
            },
          },
        },
        label: 'fetch_user_projects',
      });
      setUserProjects(result);
      console.log('User projects:', result); // Log to see the actual structure
    } catch (error) {
      console.error('Error fetching user-project associations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load user-project associations',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (hasPermission('ADMIN_USER')) {
      fetchUsers();
      fetchClients();
      fetchProjects();
      fetchUserClients();
      fetchUserProjects();
    }
  }, [selectedUserPermissions, hasPermission]);

  // Update selected clients when user is selected
  useEffect(() => {
    if (selectedUserUserClient && userClients.length > 0) {
      const userAssociations = userClients.filter(uc => uc.userId === selectedUserUserClient);
      const existingClientIds = userAssociations.map(uc => uc.clientId.toString());
      setSelectedClients(existingClientIds);
      // Store IDs of existing associations for later reference
      const existingIds = userAssociations.map(uc => uc.id);
      setExistingUserClientIds(existingIds);
    } else {
      setSelectedClients([]);
      setExistingUserClientIds([]);
    }
  }, [selectedUserUserClient, userClients]);

  // Update selected projects when user is selected
  useEffect(() => {
    if (selectedUserUserProject && userProjects.length > 0) {
      const userAssociations = userProjects.filter(up => up.userId === selectedUserUserProject);
      const existingProjectIds = userAssociations.map(up => up.projectId.toString());
      setSelectedProjects(existingProjectIds);
      // Store IDs of existing associations for later reference
      const existingIds = userAssociations.map(up => up.id);
      setExistingUserProjectIds(existingIds);
    } else {
      setSelectedProjects([]);
      setExistingUserProjectIds([]);
    }
  }, [selectedUserUserProject, userProjects]);

  const handleAssociate = async () => {
    if (!selectedUserUserClient || selectedClients.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select a user and at least one client',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Get current associations for this user
      const userAssociations = userClients.filter(uc => uc.userId === selectedUserUserClient);
      const currentClientIds = userAssociations.map(uc => uc.clientId.toString());

      // Find clients to add (selected but not in current)
      const clientsToAdd = selectedClients.filter(clientId => !currentClientIds.includes(clientId));

      // Find associations to remove (in current but not selected)
      const clientsToRemove = currentClientIds.filter(
        clientId => !selectedClients.includes(clientId)
      );
      const associationsToRemove = userAssociations.filter(
        uc =>
          uc.userId === selectedUserUserClient && clientsToRemove.includes(uc.clientId.toString())
      );

      // Create new associations
      for (const clientId of clientsToAdd) {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'userClient.create',
            payload: {
              data: {
                userId: selectedUserUserClient,
                clientId: parseInt(clientId),
              },
            },
          },
          label: 'create_user_client',
        });
      }

      // Remove deselected associations
      for (const assoc of associationsToRemove) {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'userClient.delete',
            payload: {
              id: assoc.id,
            },
          },
          label: 'delete_user_client',
        });
      }

      // Display appropriate message
      if (clientsToAdd.length > 0 && associationsToRemove.length > 0) {
        toast({
          title: 'Success',
          description: `Added ${clientsToAdd.length} and removed ${associationsToRemove.length} client associations`,
        });
      } else if (clientsToAdd.length > 0) {
        toast({
          title: 'Success',
          description: `Added ${clientsToAdd.length} client associations`,
        });
      } else if (associationsToRemove.length > 0) {
        toast({
          title: 'Success',
          description: `Removed ${associationsToRemove.length} client associations`,
        });
      } else {
        toast({
          title: 'Info',
          description: 'No changes were made to client associations',
        });
      }

      // Reset selections and refresh data
      setSelectedUserUserClient('');
      setSelectedClients([]);
      setExistingUserClientIds([]);
      fetchUserClients();
    } catch (error) {
      console.error('Error updating user-client associations:', error);
      toast({
        title: 'Error',
        description: 'Failed to update one or more associations',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAssociateProjects = async () => {
    if (!selectedUserUserProject || selectedProjects.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select a user and at least one project',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    try {
      // Get current associations for this user
      const userAssociations = userProjects.filter(up => up.userId === selectedUserUserProject);
      const currentProjectIds = userAssociations.map(up => up.projectId.toString());

      // Find projects to add (selected but not in current)
      const projectsToAdd = selectedProjects.filter(projectId => !currentProjectIds.includes(projectId));

      // Find associations to remove (in current but not selected)
      const projectsToRemove = currentProjectIds.filter(
        projectId => !selectedProjects.includes(projectId)
      );
      const associationsToRemove = userAssociations.filter(
        up =>
          up.userId === selectedUserUserProject && projectsToRemove.includes(up.projectId.toString())
      );

      // Create new associations
      for (const projectId of projectsToAdd) {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'userProject.create',
            payload: {
              data: {
                userId: selectedUserUserProject,
                projectId: parseInt(projectId),
              },
            },
          },
          label: 'create_user_project',
        });
      }

      // Remove deselected associations
      for (const assoc of associationsToRemove) {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'userProject.delete',
            payload: {
              id: assoc.id,
            },
          },
          label: 'delete_user_project',
        });
      }

      // Display appropriate message
      if (projectsToAdd.length > 0 && associationsToRemove.length > 0) {
        toast({
          title: 'Success',
          description: `Added ${projectsToAdd.length} and removed ${associationsToRemove.length} project associations`,
        });
      } else if (projectsToAdd.length > 0) {
        toast({
          title: 'Success',
          description: `Added ${projectsToAdd.length} project associations`,
        });
      } else if (associationsToRemove.length > 0) {
        toast({
          title: 'Success',
          description: `Removed ${associationsToRemove.length} project associations`,
        });
      } else {
        toast({
          title: 'Info',
          description: 'No changes were made to project associations',
        });
      }

      // Reset selections and refresh data
      setSelectedUserUserProject('');
      setSelectedProjects([]);
      setExistingUserProjectIds([]);
      fetchUserProjects();
    } catch (error) {
      console.error('Error updating user-project associations:', error);
      toast({
        title: 'Error',
        description: 'Failed to update one or more associations',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveAssociation = async (userId, clientId) => {
    const userClientToDelete = userClients.find(
      item => item.clientId === clientId && item.userId === userId
    );
    // debugger;
    setConfirmDialog({
      open: true,
      userId,
      id: userClientToDelete.id,
      clientId,
      type: 'client',
    });
  };

  const handleRemoveProjectAssociation = async (userId, projectId) => {
    const userProjectToDelete = userProjects.find(
      item => item.projectId === projectId && item.userId === userId
    );
    setConfirmDialog({
      open: true,
      userId,
      id: userProjectToDelete.id,
      projectId,
      type: 'project',
    });
  };

  const confirmRemoveAssociation = async () => {
    const { userId, clientId, projectId, id, type } = confirmDialog;

    setIsProcessing(true);
    try {
      if (type === 'client') {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'userClient.delete',
            payload: {
              id,
            },
          },
          label: 'delete_user_client',
        });

        toast({
          title: 'Success',
          description: 'Client association removed successfully',
        });

        // Refresh data
        fetchUserClients();
      } else if (type === 'project') {
        await fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'userProject.delete',
            payload: {
              id,
            },
          },
          label: 'delete_user_project',
        });

        toast({
          title: 'Success',
          description: 'Project association removed successfully',
        });

        // Refresh data
        fetchUserProjects();
      }
    } catch (error) {
      console.error('Error removing association:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove association',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ open: false, userId: null, clientId: null, projectId: null, type: null });
    }
  };

  const cancelRemoveAssociation = () => {
    setConfirmDialog({ open: false, userId: null, clientId: null, projectId: null, type: null });
  };

  // Get clients for a user
  const getClientsForUser = userId => {
    // debugger;
    const userAssociations = userClients.filter(uc => uc.userId === userId);
    const clientIds = userAssociations.map(uc => uc.clientId);
    return clients.filter(client => clientIds.includes(client.id));
  };

  // Get users for a client
  const getUsersForClient = clientId => {
    const clientAssociations = userClients.filter(uc => uc.clientId === clientId);
    const userIds = clientAssociations.map(uc => uc.userId);
    return users.filter(user => userIds.includes(user.id));
  };

  // Get projects for a user
  const getProjectsForUser = userId => {
    const userAssociations = userProjects.filter(up => up.userId === userId);
    const projectIds = userAssociations.map(up => up.projectId);
    return projects.filter(project => projectIds.includes(project.id));
  };

  // Get users for a project
  const getUsersForProject = projectId => {
    const projectAssociations = userProjects.filter(up => up.projectId === projectId);
    const userIds = projectAssociations.map(up => up.userId);
    return users.filter(user => userIds.includes(user.id));
  };

  const handleOpenPermissionDialog = user => {
    setPermissionDialog({
      open: true,
      userId: user.id,
      username: `${user.firstNameEn} ${user.lastNameEn}`,
      currentPermissions: user.permissions || [],
      newPermissions: user.permissions || [],
    });
  };

  const handleClosePermissionDialog = () => {
    setPermissionDialog({
      open: false,
      userId: null,
      username: '',
      currentPermissions: [],
      newPermissions: [],
    });
  };

  const handleUpdatePermission = async () => {
    const { userId, newPermissions, currentPermissions } = permissionDialog;

    // Check if permissions are the same
    const sortedNew = [...newPermissions].sort();
    const sortedCurrent = [...currentPermissions].sort();
    if (JSON.stringify(sortedNew) === JSON.stringify(sortedCurrent)) {
      handleClosePermissionDialog();
      return;
    }

    setIsProcessing(true);
    try {
      const result = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        throwError: true,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'user.update',
          payload: {
            id: userId,
            data: { permissions: newPermissions },
          },
        },
        label: 'update_user_permissions',
      });

      // Update the users list
      setUsers(prev =>
        prev.map(user => (user.id === userId ? { ...user, permissions: newPermissions } : user))
      );

      // If we're updating the current user's permissions, update in context
      const currentUserId = localStorage.getItem('selectedUser');
      if (result.id === currentUserId) {
        localStorage.setItem('selectedUserPermissions', JSON.stringify(newPermissions));
        setSelectedUserPermissions(newPermissions);
        setSelectedUser(result.id);
        localStorage.setItem('selectedUser', result.id);
      }
      toast({
        title: 'Success',
        description: 'User permissions updated successfully',
      });

      handleClosePermissionDialog();
    } catch (error) {
      console.error('Error updating user permissions:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user permissions',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOpenPasswordDialog = user => {
    setPasswordDialog({
      open: true,
      userId: user.id,
      username: `${user.firstNameEn} ${user.lastNameEn}`,
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
    });
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialog({
      open: false,
      userId: null,
      username: '',
      newPassword: '',
      confirmPassword: '',
      showPassword: false,
    });
  };

  const handleChangePassword = async () => {
    const { userId, newPassword, confirmPassword } = passwordDialog;

    // Validation
    if (!newPassword || !confirmPassword) {
      toast({
        title: 'Error',
        description: 'Please fill in both password fields',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: 'Error',
        description: 'Passwords do not match',
        variant: 'destructive',
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: 'Error',
        description: 'Password must be at least 6 characters',
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    try {
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        throwError: true,
        data: {
          function_name: FUNCTION_NAMES.POST.ADMIN_CHANGE_PASSWORD,
          payload: {
            userId,
            newPassword,
          },
        },
        label: 'admin_change_password',
      });

      toast({
        title: 'Success',
        description: 'Password changed successfully',
      });

      handleClosePasswordDialog();
    } catch (error) {
      console.error('Error changing password:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to change password',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Get badge variant based on permission
  const getPermissionVariant = permission => {
    switch (permission) {
      case 'ADMIN_USER':
        return 'destructive';
      case 'UPLOADER_IMAGE':
        return 'default';
      case 'FULL_EDITOR':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  // Render permission badges for multiple permissions
  const renderPermissionBadges = (permissionsArray) => {
    if (!permissionsArray || permissionsArray.length === 0) {
      return <span className="text-muted-foreground text-sm italic">No permissions</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {permissionsArray.map(perm => (
          <Badge key={perm} variant={getPermissionVariant(perm)} className="cursor-pointer hover:opacity-80">
            {perm}
          </Badge>
        ))}
      </div>
    );
  };

  if (!hasPermission('ADMIN_USER')) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">Access denied</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Confirmation Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Removal</h3>
            <p>Are you sure you want to remove this association?</p>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={cancelRemoveAssociation} disabled={isProcessing}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmRemoveAssociation}
                disabled={isProcessing}
              >
                {isProcessing ? 'Removing...' : 'Remove'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Permission Change Dialog - Multi-select */}
      {permissionDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-2">Change User Permissions</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Changing permissions for:{' '}
              <span className="font-medium">{permissionDialog.username}</span>
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Current Permissions</label>
                <div className="py-2">
                  {renderPermissionBadges(permissionDialog.currentPermissions)}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Select Permissions</label>
                <div className="border rounded-md p-3 space-y-2 max-h-60 overflow-y-auto">
                  {permissions.map(perm => (
                    <div key={perm.value} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`perm-${perm.value}`}
                        checked={permissionDialog.newPermissions.includes(perm.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPermissionDialog(prev => ({
                              ...prev,
                              newPermissions: [...prev.newPermissions, perm.value]
                            }));
                          } else {
                            setPermissionDialog(prev => ({
                              ...prev,
                              newPermissions: prev.newPermissions.filter(p => p !== perm.value)
                            }));
                          }
                        }}
                        className="h-4 w-4"
                      />
                      <label htmlFor={`perm-${perm.value}`} className="flex items-center gap-2 cursor-pointer flex-1">
                        <Shield className="h-4 w-4" />
                        <span>{perm.label}</span>
                        <Badge variant={getPermissionVariant(perm.value)} className="text-xs">
                          {perm.value}
                        </Badge>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {permissionDialog.newPermissions.length > 0 && (
                <div>
                  <label className="text-sm font-medium mb-1 block">Selected Permissions</label>
                  <div className="flex flex-wrap gap-1">
                    {permissionDialog.newPermissions.map(perm => (
                      <Badge
                        key={perm}
                        variant={getPermissionVariant(perm)}
                        className="flex items-center gap-1"
                      >
                        {perm}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-4 w-4 p-0"
                          onClick={() => {
                            setPermissionDialog(prev => ({
                              ...prev,
                              newPermissions: prev.newPermissions.filter(p => p !== perm)
                            }));
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={handleClosePermissionDialog}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdatePermission}
                disabled={
                  isProcessing ||
                  permissionDialog.newPermissions.length === 0 ||
                  JSON.stringify([...permissionDialog.newPermissions].sort()) === 
                  JSON.stringify([...permissionDialog.currentPermissions].sort())
                }
              >
                {isProcessing ? 'Updating...' : 'Update Permissions'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Dialog */}
      {passwordDialog.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <KeyRound className="h-5 w-5" />
              Change User Password
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Changing password for:{' '}
              <span className="font-medium">{passwordDialog.username}</span>
            </p>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-1 block">New Password</label>
                <div className="relative">
                  <input
                    type={passwordDialog.showPassword ? 'text' : 'password'}
                    value={passwordDialog.newPassword}
                    onChange={e =>
                      setPasswordDialog(prev => ({ ...prev, newPassword: e.target.value }))
                    }
                    placeholder="Enter new password"
                    className="w-full h-10 px-3 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="absolute right-1 top-1 h-8 w-8"
                    onClick={() =>
                      setPasswordDialog(prev => ({ ...prev, showPassword: !prev.showPassword }))
                    }
                  >
                    {passwordDialog.showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Confirm Password</label>
                <input
                  type={passwordDialog.showPassword ? 'text' : 'password'}
                  value={passwordDialog.confirmPassword}
                  onChange={e =>
                    setPasswordDialog(prev => ({ ...prev, confirmPassword: e.target.value }))
                  }
                  placeholder="Confirm new password"
                  className="w-full h-10 px-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {passwordDialog.newPassword && passwordDialog.confirmPassword && 
               passwordDialog.newPassword !== passwordDialog.confirmPassword && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}

              {passwordDialog.newPassword && passwordDialog.newPassword.length < 6 && (
                <p className="text-sm text-yellow-600">Password must be at least 6 characters</p>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={handleClosePasswordDialog}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={
                  isProcessing ||
                  !passwordDialog.newPassword ||
                  !passwordDialog.confirmPassword ||
                  passwordDialog.newPassword !== passwordDialog.confirmPassword ||
                  passwordDialog.newPassword.length < 6
                }
              >
                {isProcessing ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">User-Client Associations</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            fetchUsers();
            fetchClients();
            fetchProjects();
            fetchUserClients();
            fetchUserProjects();
          }}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Data
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create New Association</CardTitle>
          <CardDescription>Associate a user with a client to grant them access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Select User</label>
              <Select value={selectedUserUserClient} onValueChange={setSelectedUserUserClient}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.firstNameEn} {user.lastNameEn} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Select Clients</label>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full flex justify-between items-center h-10"
                  onClick={() => setClientDialogOpen(true)}
                >
                  <span className="truncate">
                    {selectedClients.length === 0
                      ? 'Select clients'
                      : `${selectedClients.length} client(s) selected`}
                  </span>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 opacity-50"
                    aria-hidden="true"
                  >
                    <path
                      d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </Button>

                {selectedClients.length > 0 && (
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto p-1 border rounded-md">
                    {selectedClients.map(clientId => {
                      const client = clients.find(c => c.id.toString() === clientId);
                      return client ? (
                        <Badge
                          key={clientId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {client.name}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-4 w-4 p-0"
                            onClick={() => {
                              setSelectedClients(prev => prev.filter(id => id !== clientId));
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Client Selection Dialog */}
              {clientDialogOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                    <h3 className="text-lg font-semibold mb-4">Select Clients</h3>

                    <div className="max-h-80 overflow-y-auto border rounded-md p-2 mb-4">
                      {clients.map(client => (
                        <div
                          key={client.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-md"
                        >
                          <input
                            type="checkbox"
                            id={`client-${client.id}`}
                            checked={selectedClients.includes(client.id.toString())}
                            onChange={() => {
                              setSelectedClients(prev => {
                                const clientId = client.id.toString();
                                if (prev.includes(clientId)) {
                                  return prev.filter(id => id !== clientId);
                                } else {
                                  return [...prev, clientId];
                                }
                              });
                            }}
                            className="h-4 w-4"
                          />
                          <label htmlFor={`client-${client.id}`} className="flex-1 cursor-pointer">
                            {client?.name}
                          </label>
                        </div>
                      ))}

                      {clients.length === 0 && (
                        <div className="text-center py-4 text-muted-foreground">
                          No clients available
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setClientDialogOpen(false)}>
                        Done
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-end">
              <Button
                className="w-full"
                onClick={handleAssociate}
                disabled={isProcessing || !selectedUserUserClient || selectedClients.length === 0}
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Associate
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create Project Association</CardTitle>
          <CardDescription>Associate a user with a project to grant them access</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Select User</label>
              <Select value={selectedUserUserProject} onValueChange={setSelectedUserUserProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.firstNameEn} {user.lastNameEn} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Select Projects</label>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full flex justify-between items-center h-10"
                  onClick={() => setProjectDialogOpen(true)}
                >
                  <span className="truncate">
                    {selectedProjects.length === 0
                      ? 'Select projects'
                      : `${selectedProjects.length} project(s) selected`}
                  </span>
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 opacity-50"
                    aria-hidden="true"
                  >
                    <path
                      d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.73379 3.0974 7.61933 3.04999 7.49999 3.04999C7.38064 3.04999 7.26618 3.0974 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.26618 11.9026 7.38064 11.95 7.49999 11.95C7.61933 11.95 7.73379 11.9026 7.81819 11.8182L10.0682 9.56819Z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </Button>

                {selectedProjects.length > 0 && (
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto p-1 border rounded-md">
                    {selectedProjects.map(projectId => {
                      const project = projects.find(p => p.id.toString() === projectId);
                      return project ? (
                        <Badge
                          key={projectId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {project.name}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-4 w-4 p-0"
                            onClick={() => {
                              setSelectedProjects(prev => prev.filter(id => id !== projectId));
                            }}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>

              {/* Project Selection Dialog */}
              {projectDialogOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
                    <h3 className="text-lg font-semibold mb-4">Select Projects</h3>

                    <div className="max-h-80 overflow-y-auto border rounded-md p-2 mb-4">
                      {projects.map(project => (
                        <div
                          key={project.id}
                          className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-md"
                        >
                          <input
                            type="checkbox"
                            id={`project-${project.id}`}
                            checked={selectedProjects.includes(project.id.toString())}
                            onChange={() => {
                              setSelectedProjects(prev => {
                                const projectId = project.id.toString();
                                if (prev.includes(projectId)) {
                                  return prev.filter(id => id !== projectId);
                                } else {
                                  return [...prev, projectId];
                                }
                              });
                            }}
                            className="h-4 w-4"
                          />
                          <label htmlFor={`project-${project.id}`} className="flex-1 cursor-pointer">
                            {project?.name}
                          </label>
                        </div>
                      ))}

                      {projects.length === 0 && (
                        <div className="text-center py-4 text-muted-foreground">
                          No projects available
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setProjectDialogOpen(false)}>
                        Done
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-end">
              <Button
                className="w-full"
                onClick={handleAssociateProjects}
                disabled={isProcessing || !selectedUserUserProject || selectedProjects.length === 0}
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Associate
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="users" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="users" className="flex items-center">
            <Users className="h-4 w-4 mr-2" />
            View by Users
          </TabsTrigger>
          <TabsTrigger value="clients" className="flex items-center">
            <Building2 className="h-4 w-4 mr-2" />
            View by Clients
          </TabsTrigger>
          <TabsTrigger value="projects" className="flex items-center">
            <FolderOpen className="h-4 w-4 mr-2" />
            View by Projects
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Users and Their Associated Clients</CardTitle>
              <CardDescription>View and manage client associations for each user</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="text-center py-4">Loading users...</div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Permission</TableHead>
                        <TableHead>Associated Clients</TableHead>
                        <TableHead>Associated Projects</TableHead>
                        <TableHead className="w-[220px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map(user => {
                        const clientsForUser = getClientsForUser(user.id);
                        const projectsForUser = getProjectsForUser(user.id);
                        return (
                          <TableRow key={user.id}>
                            <TableCell>
                              {user.firstNameEn} {user.lastNameEn}
                            </TableCell>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{renderPermissionBadges(user.permissions)}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                {clientsForUser.length === 0 ? (
                                  <span className="text-muted-foreground text-sm italic">
                                    No clients assigned
                                  </span>
                                ) : (
                                  clientsForUser.map(client => (
                                    <Badge key={client.id} className="flex items-center gap-1">
                                      {client.name}
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-4 w-4 p-0 ml-1"
                                        onClick={() => handleRemoveAssociation(user.id, client.id)}
                                        disabled={isProcessing}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </Badge>
                                  ))
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex flex-wrap gap-2">
                                  {projectsForUser.length === 0 ? (
                                    <span className="text-muted-foreground text-sm italic">
                                      No projects assigned
                                    </span>
                                  ) : (
                                    projectsForUser.map(project => (
                                      <Badge key={project.id} variant="outline" className="flex items-center gap-1">
                                        {project.name}
                                        <Button
                                          size="icon"
                                          variant="ghost"
                                          className="h-4 w-4 p-0 ml-1"
                                          onClick={() => handleRemoveProjectAssociation(user.id, project.id)}
                                          disabled={isProcessing}
                                        >
                                          <X className="h-3 w-3" />
                                        </Button>
                                      </Badge>
                                    ))
                                  )}
                                </div>
                              </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={() => handleOpenPermissionDialog(user)}
                                >
                                  <UserCog className="h-3 w-3" />
                                  Permissions
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex items-center gap-1"
                                  onClick={() => handleOpenPasswordDialog(user)}
                                >
                                  <KeyRound className="h-3 w-3" />
                                  Password
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clients">
          <Card>
            <CardHeader>
              <CardTitle>Clients and Their Associated Users</CardTitle>
              <CardDescription>View and manage user access for each client</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingClients ? (
                <div className="text-center py-4">Loading clients...</div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead>Client</TableHead>
                        <TableHead>ID</TableHead>
                        <TableHead>Associated Users</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {clients.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center">
                          No clients found
                        </TableCell>
                      </TableRow>
                    ) : (
                      clients.map(client => {
                        const clientUsers = getUsersForClient(client.id);
                        return (
                          <TableRow key={client.id}>
                            <TableCell>{client.name}</TableCell>
                            <TableCell>{client.id}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                {clientUsers.length === 0 ? (
                                  <span className="text-muted-foreground text-sm italic">
                                    No users assigned
                                  </span>
                                ) : (
                                  clientUsers.map(user => (
                                    <Badge key={user.id} className="flex items-center gap-1">
                                      {user.firstNameEn} {user.lastNameEn} ({user.email})
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-4 w-4 p-0 ml-1"
                                        onClick={() => handleRemoveAssociation(user.id, client.id)}
                                        disabled={isProcessing}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </Badge>
                                  ))
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="projects">
          <Card>
            <CardHeader>
              <CardTitle>Projects and Their Associated Users</CardTitle>
              <CardDescription>View and manage user access for each project</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingProjects ? (
                <div className="text-center py-4">Loading projects...</div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto border rounded-md">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead>Project</TableHead>
                        <TableHead>ID</TableHead>
                        <TableHead>Associated Users</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                    {projects.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center">
                          No projects found
                        </TableCell>
                      </TableRow>
                    ) : (
                      projects.map(project => {
                        const projectUsers = getUsersForProject(project.id);
                        return (
                          <TableRow key={project.id}>
                            <TableCell>{project.name}</TableCell>
                            <TableCell>{project.id}</TableCell>
                            <TableCell>
                              <div className="flex flex-wrap gap-2">
                                {projectUsers.length === 0 ? (
                                  <span className="text-muted-foreground text-sm italic">
                                    No users assigned
                                  </span>
                                ) : (
                                  projectUsers.map(user => (
                                    <Badge key={user.id} className="flex items-center gap-1">
                                      {user.firstNameEn} {user.lastNameEn} ({user.email})
                                      <Button
                                        size="icon"
                                        variant="ghost"
                                        className="h-4 w-4 p-0 ml-1"
                                        onClick={() => handleRemoveProjectAssociation(user.id, project.id)}
                                        disabled={isProcessing}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </Badge>
                                  ))
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserClientsManager;
